import type { FC, FormEvent } from 'react';
import React, { useEffect, useState } from 'react';
import { groupsApi } from 'src/shared/api/groupsApi';
import { setError, setSuccess } from 'src/shared/lib/utils';
import type { GroupProject } from 'src/shared/types/groups';
import { Button, Modal } from 'src/shared/ui';

import { validateSelection } from '../lib/validation';

const nativeFieldClassName =
  'font-gothampro-400 h-11 w-full rounded border border-violet-500 bg-white px-4 text-black ' +
  'transition-all focus:border-blue-100 focus:outline-none';

const labelClassName = 'font-gothampro-400 mb-1 ml-1 text-xs leading-3 text-grey-300';
const errorClassName = 'font-gothampro-400 mt-1 text-extra-xs text-pink-100';

interface ProjectsTabProps {
  groupId: number;
  projects: GroupProject[];
  onUpdate: () => void | Promise<void>;
}

export const ProjectsTab: FC<ProjectsTabProps> = ({ groupId, projects, onUpdate }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [availableProjects, setAvailableProjects] = useState<GroupProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectionError, setSelectionError] = useState('');

  const fetchProjects = async () => {
    try {
      const { data } = await groupsApi.getProjects();
      setAvailableProjects(data);
    } catch (error) {
      setError('Ошибка при загрузке проектов');
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedProjectId('');
    setSelectionError('');
  };

  const handleAddProject = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = validateSelection(selectedProjectId, 'Выберите проект');
    setSelectionError(error);

    if (error) {
      return;
    }

    try {
      await groupsApi.addGroupProject(groupId, Number(selectedProjectId));
      setSuccess('Проект добавлен успешно');
      await onUpdate();
      closeModal();
    } catch (error) {
      setError('Ошибка при добавлении проекта');
    }
  };

  const handleRemoveProject = async (projectId: number) => {
    try {
      await groupsApi.removeGroupProject(groupId, projectId);
      setSuccess('Проект удален из группы');
      await onUpdate();
    } catch (error) {
      setError('Ошибка при удалении проекта');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="border-b p-4">
        <Button classNames="w-auto" onClick={() => setIsModalVisible(true)} variant="green">
          Добавить проект
        </Button>
      </div>

      <div className="overflow-auto p-4">
        <table className="min-w-full divide-y divide-grey-1200">
          <thead className="bg-grey-1100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Название</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Описание</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Создан</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Видимость</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-1200 bg-white">
            {projects.length ? (
              projects.map(project => (
                <tr key={project.id}>
                  <td className="px-4 py-3 text-sm text-black">{project.project_name}</td>
                  <td className="px-4 py-3 text-sm text-black">
                    {project.project_description || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-black">{project.created_dt || '—'}</td>
                  <td className="px-4 py-3 text-sm text-black">
                    {project.is_visible ? 'Да' : 'Нет'}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      classNames="w-auto px-4"
                      onClick={() => handleRemoveProject(project.id)}
                      size="small"
                      variant="orange"
                    >
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-center text-sm text-grey-300" colSpan={5}>
                  В группе пока нет проектов
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalVisible && (
        <Modal classNames="z-130 bg-black/40 px-4" onClose={closeModal}>
          <div className="w-[min(90vw,30rem)]">
            <h2 className="mb-6 text-xl font-semibold text-black">Добавить проект</h2>

            <form className="flex flex-col gap-4" onSubmit={handleAddProject}>
              <div>
                <div className={labelClassName}>Проект</div>
                <select
                  className={nativeFieldClassName}
                  onChange={event => {
                    setSelectedProjectId(event.target.value);
                    setSelectionError('');
                  }}
                  value={selectedProjectId}
                >
                  <option value="">Выберите проект</option>
                  {availableProjects.map(project => (
                    <option key={project.id} value={String(project.id)}>
                      {project.project_name}
                    </option>
                  ))}
                </select>
                {selectionError && <div className={errorClassName}>{selectionError}</div>}
              </div>

              <div className="mt-2 flex justify-end gap-3">
                <Button classNames="w-auto px-5" onClick={closeModal} type="button" variant="grey">
                  Отмена
                </Button>
                <Button classNames="w-auto px-5" type="submit" variant="green">
                  Добавить
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};
